import { Link, useNavigate, useParams } from 'react-router-dom';
import { LinkIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { TagInput } from '../ui/TagInput';
import { ChipSelect } from '../ui/ChipSelect';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { CheckList } from '../ui/CheckList';
import { NumberInput } from '../ui/NumberInput';
import { ViewOnSite } from './ViewOnSite';
import { PromoPanel } from './PromoPanel';
import { ProductMediaPanel } from './ProductMediaPanel';
import { StockField } from './StockField';
import { useEditorItem } from './useEditorItem';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { productSeoChecks } from './catalogSeo';
import { productSeoAuto } from '../../utils/seoAuto';
import { skinTypes } from '../../data/concerns';
import { PRODUCT_NOTE_MAX, TAG_SUGGESTIONS, emptyProduct, oneOf, productCategories, productChecks, routineSteps, uniqueSorted } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
const back = {
  to: '/be/producten',
  label: 'Alle producten'
};

/** Eén product: basis, huidthema's, teksten, koppelingen en voorraad. */
export function ProductEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: product,
    set
  } = useEditorItem('products', id, emptyProduct);
  if (!product) return <MissingItem title="Product niet gevonden" back={back} data-id="element-667" data-mp="32pccmrjjx6ul" />;
  const takenIds = catalog.products.map(p => p.id);
  const suggestedId = uniqueSlug(slugify(`${product.brand} ${product.name}`), takenIds);
  // Een zelf getypt webadres dat al bestaat zou dat andere product overschrijven.
  const idTaken = isNew && !!product.id && takenIds.includes(product.id);
  const canAdd = product.name.trim() !== '' && product.price > 0 && !idTaken;
  const checks = [...productChecks(product), ...productSeoChecks(product, catalog.products)];
  const inRoutines = index.routinesWithProduct(product.id);
  const inArticles = catalog.articles.filter(a => a.productIds?.includes(product.id));
  // Verwijderen haalt hem ook uit protocollen en journalstukken; een protocol kan dan onder de twee zakken.
  const usedIn = [inRoutines.length ? plural(inRoutines.length, 'protocol', 'protocollen') : '', inArticles.length ? plural(inArticles.length, 'journalstuk', 'journalstukken') : ''].filter(Boolean);
  const thinRoutines = inRoutines.filter(r => r.productIds.length <= 2);
  const concerns = index.concernsForProduct(product);
  const allIngredients = uniqueSorted(catalog.products.map(p => p.keyIngredients));
  const add = () => {
    if (!canAdd) return;
    const created = {
      ...product,
      id: product.id ? uniqueSlug(product.id, takenIds) : suggestedId
    };
    putItem('products', created);
    navigate(`/be/producten/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-668" data-mp="3it1yvqubu8vy">
      <PageHeader title={isNew ? 'Nieuw product' : product.name || 'Naamloos product'} description={isNew ? 'Vul in wat je weet — lege velden verbergt de site vanzelf.' : `${product.brand} · ${product.category}`} back={back} actions={isNew ? <span className="flex flex-wrap items-center justify-end gap-3" data-id="element-670" data-mp="34x81a3z075y5">
              {!canAdd && <span className="text-[13px] text-muted" data-id="element-671" data-mp="1340b1zhur3xh">
                  {!product.name.trim() ? 'Vul een naam in' : !(product.price > 0) ? 'Vul een prijs in' : 'Dit webadres bestaat al'}
                </span>}
              <button type="button" onClick={add} disabled={!canAdd} className={btnPrimary} data-id="element-672" data-mp="3rwndrx0wgdch">
                Product toevoegen
              </button>
            </span> : <>
              <DeleteButton undoable warning={usedIn.length ? `Zit in ${usedIn.join(' en ')}. Wordt daar ook weggehaald.${thinRoutines.length ? ` ${thinRoutines.map(r => r.name).join(', ')} houdt dan minder dan twee producten over.` : ''}` : undefined} onConfirm={() => {
        const restore = removeItem('products', product.id);
        navigate(back.to);
        undoToast(`“${product.name || 'Naamloos product'}” verwijderd`, restore, {
          description: usedIn.length ? `Ongedaan maken zet hem ook terug in ${usedIn.join(' en ')}.` : 'Pas definitief als je opslaat.',
          restored: 'Product teruggezet'
        });
      }} data-id="element-673" data-mp="3hzipnrnhu7t1" />
              <ViewOnSite path={`/webshop/${product.id}`} data-id="element-674" data-mp="26mjfaeay7r4x" />
            </>} data-id="element-669" data-mp="3itbo3c7jsc0l" />

      {/* Eén foto-paneel, door het grid geplaatst: op mobiel bovenaan (daar herken je het product
          aan), vanaf lg bovenaan de zijkolom. De `1fr`-rij vangt de hoogte van de lange formulierkolom
          op, zodat er geen gat valt tussen de foto en de rest van de zijkolom. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:grid-rows-[auto_1fr]" data-id="element-675" data-mp="2pl5gdt6gti8u">
        <Panel title="Productfoto" description="Een packshot op wit of transparant werkt het best." className="lg:col-span-4 lg:col-start-9 lg:row-start-1" data-id="element-676" data-mp="33c0pn6kvm1cy">
          <div className="mx-auto max-w-[14rem] lg:max-w-none" data-id="element-677" data-mp="3da7x8yt9z72a">
            <ImageField value={product.image} onChange={image => set({
            image
          })} data-id="element-678" data-mp="3daga4qpeecdh" />
          </div>
        </Panel>

        <div className="min-w-0 space-y-6 lg:col-span-8 lg:col-start-1 lg:row-span-2 lg:row-start-1" data-id="element-679" data-mp="389aeol4csb09">
          <Panel title="Basis" data-id="element-680" data-mp="3fcgz1ff2p1b9">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-681" data-mp="3kcv4cppahx5l">
              <Field label="Naam" className="sm:col-span-2" data-id="element-682" data-mp="3kbbsrcekwaa5">
                <input value={product.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} placeholder="Bijv. Crystal Retinal 1" data-id="element-683" data-mp="3pbg64tk6wow5" />
              </Field>
              <Field label="Merk" data-id="element-684" data-mp="2xg31uqd0zuq">
                <select value={product.brand} onChange={e => set({
                brand: e.target.value
              })} className={inputCls} data-id="element-685" data-mp="2wdc5mt1exet">
                  {catalog.brands.map(b => <option key={b.name} data-id="element-686" data-mp="bx31q8rpfrk3">{b.name}</option>)}
                </select>
              </Field>
              <Field label={product.promo ? 'Actieprijs (€, incl. btw)' : 'Prijs (€, incl. btw)'} hint={product.promo ? 'Pas je aan bij Leveranciersactie.' : 'Adviesprijs van het merk, nooit korting.'} data-id="element-687" data-mp="gvd1kwodjgc1">
                <NumberInput decimals value={product.price || undefined} onChange={price => set({
                price: price ?? 0
              })} emptyMessage="Vul een prijs in, bijv. 49,95." placeholder="49,95" data-id="element-688" data-mp="2b4jkvrjv3buq" />
              </Field>
              <Field label="Soort product" data-id="element-689" data-mp="1wg5j7jy6ltfr">
                <select value={product.category} onChange={e => {
                const category = oneOf(productCategories, e.target.value);
                if (category) set({
                  category
                });
              }} className={inputCls} data-id="element-690" data-mp="1wh6spjzju7vd">
                  {productCategories.map(c => <option key={c} data-id="element-691" data-mp="2f6wmq4vhmpm8">{c}</option>)}
                </select>
              </Field>
              <Field label="Stap in de routine" hint="Bepaalt de volgorde en het filter in de webshop." data-id="element-692" data-mp="18r48tr5sg87u">
                <select value={product.step ?? ''} onChange={e => set({
                step: oneOf(routineSteps, e.target.value)
              })} className={inputCls} data-id="element-693" data-mp="13q6rthwilk7x">
                  <option value="" data-id="element-694" data-mp="1ye6vwcayhe3l">{product.step ? 'Geen stap' : 'Kies een stap'}</option>
                  {routineSteps.map(s => <option key={s} data-id="element-695" data-mp="1kf744vs5cj37">{s}</option>)}
                </select>
              </Field>
              <Field label="Inhoud" hint="Bijv. 30 ml. Leeg = niet getoond." data-id="element-696" data-mp="1ogseoah7kla8">
                <input value={product.volume ?? ''} onChange={e => set({
                volume: e.target.value || undefined
              })} className={inputCls} placeholder="30 ml" data-id="element-697" data-mp="2c5bmyq09cy2b" />
              </Field>
              <Field label="Gaat mee" hint="Bijv. ± 3 maanden. Leeg = niet getoond." data-id="element-698" data-mp="23djgsri9ob3j">
                <input value={product.usesPerBottle ?? ''} onChange={e => set({
                usesPerBottle: e.target.value || undefined
              })} className={inputCls} placeholder="± 3 maanden" data-id="element-699" data-mp="27evap986bihj" />
              </Field>
            </div>
          </Panel>

          <Panel title="Huidthema’s" description="Hiermee verschijnt het product in de filters, de klachtenkiezer en het huidadvies." data-id="element-700" data-mp="ofoyqoywp1cs">
            <ChipSelect options={skinTypes} value={product.skinTypes} onChange={next => set({
            skinTypes: next
          })} meta={s => String(index.productsForSkinType(s).filter(p => p.id !== product.id).length)} data-id="element-701" data-mp="jgkrw4zgffpm" />
            {concerns.length > 0 && <p className="mt-4 flex items-start gap-2 text-[13px] leading-relaxed text-muted" data-id="element-702" data-mp="5jfd9glod8ir">
                <LinkIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-703" data-mp="k2ub6xxcllg" />
                Wordt aanbevolen bij: {concerns.map(c => c.label.toLowerCase()).join(', ')}.
              </p>}
          </Panel>

          <Panel title="Teksten" description="In je eigen woorden — zo staat het op de productpagina." data-id="element-704" data-mp="1qtw7k3ibc8rj">
            <div className="space-y-5" data-id="element-705" data-mp="1luje2zwxs3fz">
              <Field label="Korte omschrijving" hint="Eén zin onder de naam, op de productkaart." count={{
              value: product.note.length,
              max: PRODUCT_NOTE_MAX
            }} data-id="element-706" data-mp="1lxp1mapx2x9l">
                <input value={product.note} onChange={e => set({
                note: e.target.value
              })} className={inputCls} data-id="element-707" data-mp="2c4ib92s504en" />
              </Field>
              <Field label="Waarom ik dit kies" hint="Je persoonlijke verhaal: voor wie, wat je ziet in de salon." data-id="element-708" data-mp="27ae1ycvefkae">
                <textarea value={product.why} onChange={e => set({
                why: e.target.value
              })} rows={5} className={`${inputCls} resize-y leading-relaxed`} data-id="element-709" data-mp="1xeb3pi3o45sq" />
              </Field>
              <Field label="Zo gebruik je het" data-id="element-710" data-mp="1tesh8hw77mns">
                <textarea value={product.howTo} onChange={e => set({
                howTo: e.target.value
              })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-711" data-mp="1octdv80gkpqu" />
              </Field>
              <Field label="Belangrijkste ingrediënten" as="div" data-id="element-712" data-mp="3irdv1apkm35h">
                <TagInput label="Belangrijkste ingrediënten" value={product.keyIngredients} onChange={keyIngredients => set({
                keyIngredients
              })} suggestions={allIngredients.filter(i => !product.keyIngredients.includes(i)).slice(0, TAG_SUGGESTIONS)} data-id="element-713" data-mp="3ipjjgn06vne2" />
              </Field>
            </div>
          </Panel>

          <ProductMediaPanel value={product.media ?? []} onChange={media => set({
          media
        })} data-id="element-714" data-mp="3v3ydzcu9wwq5" />

          <Panel title="Adviseer ik hierbij" description="Verschijnt als “Samen versterken ze elkaar” op de productpagina en in de winkelmand." data-id="element-715" data-mp="3usprhev7x1p">
            <ProductPicker value={product.pairs} onChange={pairs => set({
            pairs
          })} products={catalog.products} exclude={[product.id]} emptyText="Nog geen producten die je hierbij adviseert." data-id="element-716" data-mp="37brazuu1lrhr" />
          </Panel>

          {product.kind !== 'giftcard' && <SeoPanel seo={product.seo} onChange={seo => set({
          seo
        })} auto={productSeoAuto(product)} path={product.id ? `/webshop/${product.id}` : '/webshop'} imageFallback="de productfoto" data-id="element-717" data-mp="rkn1fvng7fkd" />}
        </div>

        <aside aria-label="Actie, controle en zichtbaarheid" className="min-w-0 space-y-6 lg:col-span-4 lg:col-start-9 lg:row-start-2" data-id="element-718" data-mp="2no1gis84rqwx">
          <PromoPanel product={product} set={set} data-id="element-719" data-mp="2xmqn3ouktoxv" />

          <Panel title="Wat mist er nog?" data-id="element-720" data-mp="2xmyz7eivkq80">
            <CheckList checks={checks} okText="Dit product is compleet" data-id="element-721" data-mp="29wwe5iddokiw" />
          </Panel>

          <Panel title="Zichtbaarheid" data-id="element-722" data-mp="24xs46pimqiri">
            <div className="space-y-1" data-id="element-723" data-mp="24yurkngqu32o">
              <Toggle checked={Boolean(product.bestseller)} onChange={bestseller => set({
              bestseller
            })} label="Bestseller" description="Op de homepage in “Liever met één product beginnen?”." data-id="element-724" data-mp="24vp5lh3evhb6" />
              <Toggle checked={Boolean(product.salonFavourite)} onChange={salonFavourite => set({
              salonFavourite
            })} label="Favoriet uit de salon" description="Zelfde plekken op de homepage als een bestseller. Je eigen plank op Over Sarah kies je los, in Paginateksten › Over Sarah." data-id="element-725" data-mp="1q7cgn42qqyq6" />
              <Toggle checked={product.refill} onChange={refill => set({
              refill
            })} label="Automatisch bij te vullen" description="Klant kan het in het Bewust Abonnement zetten." data-id="element-726" data-mp="125pj6jg9xoyq" />
            </div>
          </Panel>

          {inRoutines.length > 0 && <Panel title="Zit in protocollen" data-id="element-727" data-mp="1zsfxwnvzl1ek">
              <ul className="space-y-2 text-[14px]" data-id="element-728" data-mp="1kvxj6soofzap">
                {inRoutines.map(r => <li key={r.id} data-id="element-729" data-mp="1pui8vy6az0kw">
                    <Link to={`/be/protocollen/${r.id}`} className="text-ink underline-offset-4 hover:text-clay hover:underline" data-id="element-730" data-mp="1ptqm9cuood6r">
                      {r.name}
                    </Link>
                    <span className="text-muted" data-id="element-731" data-mp="efv8o8jfgchn"> · {euro(index.routineTotals(r).price)}</span>
                  </li>)}
              </ul>
              <p className="mt-3 text-[12px] text-muted" data-id="element-732" data-mp="uoko5a00bnuj">De protocolprijs past zich vanzelf aan je prijs aan.</p>
            </Panel>}

          <Panel title="Koppeling & voorraad" data-id="element-733" data-mp="kw584i89t9ca">
            <div className="space-y-4" data-id="element-734" data-mp="kuu3kg703xk4">
              <Field label="Webadres" hint={idTaken ? 'Dit webadres is al van een ander product. Kies een ander.' : isNew ? 'Wordt vanzelf gemaakt uit merk en naam.' : 'Staat vast, zodat oude links en Google blijven werken.'} data-id="element-735" data-mp="fydd3fcbx73v">
                <input value={isNew ? product.id || suggestedId : product.id} onChange={e => isNew && set({
                id: slugify(e.target.value)
              })} disabled={!isNew} className={`${inputCls} font-mono text-[13px]`} data-id="element-736" data-mp="yous6h48o46r" />
              </Field>
              <Field label="Omschrijving van de foto" hint="Voor blinde bezoekers en Google. Leeg = merk en naam." data-id="element-737" data-mp="1yxksf3kpvbpx">
                <input value={product.imageAlt ?? ''} onChange={e => set({
                imageAlt: e.target.value || undefined
              })} className={inputCls} data-id="element-738" data-mp="23w5s91w23tdu" />
              </Field>
              <Field label="Barcode (EAN)" hint="Staat op de doos. Helpt Google Shopping." data-id="element-739" data-mp="2dlxr48b6v8o4">
                <input value={product.gtin ?? ''} onChange={e => set({
                gtin: e.target.value.replace(/\D/g, '') || undefined
              })} inputMode="numeric" maxLength={14} className={`${inputCls} font-mono text-[13px]`} data-id="element-740" data-mp="2iksfdprxdpfm" />
              </Field>
              {isNew ? <Field label="Beginvoorraad" hint="Leeg = niet bijhouden. Daarna pas je de voorraad hier los aan." data-id="element-741" data-mp="2wi7936p7ojde">
                  <NumberInput allowEmpty value={product.stock} onChange={stock => set({
                stock
              })} placeholder="Niet bijhouden" data-id="element-742" data-mp="12694ji1ukbnu" />
                </Field> : <StockField key={product.id} productId={product.id} stock={product.stock} data-id="element-743" data-mp="u7nlxjryg6p2" />}
              {/* Technisch nummer: vult de koppeling zelf in. Alleen tonen als het er is, zodat niemand het met de hand gaat typen. */}
              {product.wooId !== undefined && <p className="text-[12px] text-muted" data-id="element-744" data-mp="z3crq4ukijtt">
                  WooCommerce-nummer <span className="font-mono text-ink" data-id="element-745" data-mp="1d348gdcgyebi">{product.wooId}</span>
                </p>}
            </div>
          </Panel>
        </aside>
      </div>
    </div>;
}